// DEPENDENCIES
import isEmpty from 'lodash/isEmpty';

// UTILS
import {defaultErrorHandler} from "commons/js/utils/default-error-handler";
import {HTTP_UNPROCESSABLE_ENTITY} from "commons/js/HttpStatusCodes";

/**
 * This mixin is intended to handle server-based validations.
 * It should be used, when it's necessary to perform some validation within the
 * back-end, retrieving the error messages returned or displaying a toast whenever
 * the error wasn't caused by the user input (status code differs from 422 - unprocessable entity).
 */
export default {
  data() {
    return {
      isTyping: false,
      serverValidation: {
        isValid: undefined,
        standAloneMessage: null,
        fields: {}
      }
    }
  },

  computed: {
    /**
     * Gets the server validation state.
     *
     * @returns {boolean}
     */
    serverValidationHasFailed() {
      return this.serverValidation.isValid === false;
    },

    /**
     * Checks whenever the user is not typing anymore and the server validation has
     * failed, in this case, some server-based field is invalid.
     *
     * @returns {boolean}
     */
    hasInvalidFieldsOnServer() {
      return this.isTyping === false && this.serverValidationHasFailed;
    }
  },

  methods: {
    handleFocus() {
      this.isTyping = true;
    },

    /**
     * It sets the validations received from server.
     *
     * @param {object|undefined} errors The incoming errors data or undefined when they don't exist.
     * @param {string|undefined} message The standalone message returned by server or undefined when it doesn't exists.
     * @returns {void}
     */
    setServerValidations(errors, message) {
      this.serverValidation.isValid = false;

      if (errors) {
        for (let field in errors) {
          this.serverValidation.fields[field] = errors[field];
        }

        return;
      }

      this.serverValidation.standAloneMessage = message;
    },

    /**
     * Resets the server validations, in order to get it in an initial state to
     * the next submit.
     *
     * @returns {void}
     */
    resetServerValidations() {
      this.serverValidation.isValid = undefined;
      this.serverValidation.standAloneMessage = null;
      this.serverValidation.fields = {};
    },

    /**
     * Emits a toast call when it's called as callback when an error occurs.
     *
     * @param {object} error The error data.
     * @returns {void}
     */
    defaultCallbackOnError(error) {
      this.$emit('handle-toast', defaultErrorHandler(error));
    },

    /**
     * It handles the error captured from the authentication request.
     *
     * @param {object} error The error information.
     * @param {function} [callback] An optional callback to be executed.
     * @returns {void}
     */
    handleError(error, callback = (error) => this.defaultCallbackOnError(error)) {
      const {response} = error;

      if (
        response.data &&
        (response.data.errors || response.data.message) &&
        response.status === HTTP_UNPROCESSABLE_ENTITY
      ) {
        this.setServerValidations(response.data.errors, response.data.message);

        return;
      }

      callback(error);
    },

    /**
     * Gets the error message returned by server validation within
     * the given field name.
     *
     * @param {string} fieldName The server-based field name.
     * @returns {string|null} The error message returned by server validation or null in such cases where there are no errors.
     * @throws Will throw an error if the given field name doesn't match any server-based field.
     */
    getErrorMessageByField(fieldName) {
      if (isEmpty(this.serverValidation.fields)) {
        return null;
      };

      if (!this.serverValidation.fields[fieldName]) {
        throw new Error(`The given "${fieldName}" field name doesn't match any server-based field. Please, review your validation.`);
      }

      const messages = this.serverValidation.fields[fieldName];

      return messages.length > 1 ? messages.join("\r\n") : messages[0];
    }
  },
};
